export const SAMPLE_PAYMENT_APR_PERCENT = 5.99;
export const SAMPLE_PAYMENT_DOWN_PERCENT = 10;

export const PAYMENT_PREFERENCES_STORAGE_KEY = "thiscar-payment-preferences";

export const PAYMENT_CREDIT_PROFILES = {
    excellent: {
        label: "Excellent",
        range: "750–850",
        aprPercent: 5.99,
        description: "Strongest credit range",
        spread: 10
    },
    good: {
        label: "Good",
        range: "700–749",
        aprPercent: 8.99,
        description: "Established credit",
        spread: 10
    },
    fair: {
        label: "Fair",
        range: "640–699",
        aprPercent: 12.99,
        description: "Some credit challenges",
        spread: 15
    },
    building: {
        label: "Building credit",
        range: "Below 640",
        aprPercent: 17.99,
        description: "A fresh start",
        spread: 15
    }
} as const;

export const PAYMENT_LOAN_TERMS = [48, 60, 72, 84] as const;

export type PaymentCreditProfileId = keyof typeof PAYMENT_CREDIT_PROFILES;
export type PaymentLoanTerm = (typeof PAYMENT_LOAN_TERMS)[number];

/**
 * Shape persisted under PAYMENT_PREFERENCES_STORAGE_KEY, shared by the VDP
 * pricing panel and the SRP shop-by-payment drawer/cards.
 */
export interface StoredPaymentPreferences {
    down: number;
    term: PaymentLoanTerm;
    tier: PaymentCreditProfileId;
    customized: boolean;
}

export function isPaymentCreditProfileId(value: unknown): value is PaymentCreditProfileId {
    return typeof value === "string" && value in PAYMENT_CREDIT_PROFILES;
}

export function isPaymentLoanTerm(value: unknown): value is PaymentLoanTerm {
    return typeof value === "number" && (PAYMENT_LOAN_TERMS as readonly number[]).includes(value);
}

export interface PaymentEstimateInput {
    advertisedPrice: number;
    downPayment: number;
    termMonths: PaymentLoanTerm;
    creditProfileId: PaymentCreditProfileId;
}

export interface PaymentEstimateResult {
    aprPercent: number;
    monthlyPayment: number;
    minimumMonthlyPayment: number;
    maximumMonthlyPayment: number;
}

export interface EstimatedPaymentRange {
    aprPercent: number;
    downPaymentPercent: number;
    termMonths: PaymentLoanTerm;
    monthlyPayment: number;
    minimumMonthlyPayment: number;
    maximumMonthlyPayment: number;
}

interface AffordableVehiclePriceInput {
    maximumMonthlyPayment: number;
    downPayment: number;
    termMonths: number;
    aprPercent: number;
}

export function getSampleLoanTerm(year: number): EstimatedPaymentRange["termMonths"] {
    if (year <= 2015) return 48;
    if (year <= 2024) return 72;
    return 84;
}

/**
 * Mirrors the VDP proof's payment estimator: a standard amortized loan with a
 * fixed sample APR and a small tier-specific range around the rounded payment.
 */
export function calculatePaymentEstimate({
    advertisedPrice,
    downPayment,
    termMonths,
    creditProfileId
}: PaymentEstimateInput): PaymentEstimateResult | null {
    if (
        !Number.isFinite(advertisedPrice) ||
        advertisedPrice <= 0 ||
        !Number.isFinite(downPayment) ||
        downPayment < 0 ||
        !PAYMENT_LOAN_TERMS.includes(termMonths)
    ) {
        return null;
    }

    const profile = PAYMENT_CREDIT_PROFILES[creditProfileId];
    if (!profile) return null;

    const principal = Math.max(0, advertisedPrice - downPayment);
    const monthlyRate = profile.aprPercent / 100 / 12;
    const growth = (1 + monthlyRate) ** termMonths;
    const monthlyPayment = Math.round(principal * monthlyRate * (growth / (growth - 1)));

    return {
        aprPercent: profile.aprPercent,
        monthlyPayment,
        minimumMonthlyPayment: Math.max(0, monthlyPayment - profile.spread),
        maximumMonthlyPayment: monthlyPayment + profile.spread
    };
}

/**
 * Provides a shopping estimate only. The fixed sample assumptions mirror the
 * approved SRP proof; a lender determines actual rates, terms, and payments.
 */
export function getEstimatedPaymentRange(advertisedPrice: number, vehicleYear: number): EstimatedPaymentRange | null {
    if (!Number.isFinite(advertisedPrice) || advertisedPrice <= 0) return null;

    const termMonths = getSampleLoanTerm(vehicleYear);
    const estimate = calculatePaymentEstimate({
        advertisedPrice,
        downPayment: advertisedPrice * (SAMPLE_PAYMENT_DOWN_PERCENT / 100),
        termMonths,
        creditProfileId: "excellent"
    });
    if (!estimate) return null;

    return {
        aprPercent: SAMPLE_PAYMENT_APR_PERCENT,
        downPaymentPercent: SAMPLE_PAYMENT_DOWN_PERCENT,
        termMonths,
        monthlyPayment: estimate.monthlyPayment,
        minimumMonthlyPayment: estimate.minimumMonthlyPayment,
        maximumMonthlyPayment: estimate.maximumMonthlyPayment
    };
}

/**
 * Inverts the standard amortized-loan formula to estimate the highest vehicle
 * price supported by a shopper's monthly budget and sample assumptions.
 */
export function getMaximumAffordableVehiclePrice({
    maximumMonthlyPayment,
    downPayment,
    termMonths,
    aprPercent
}: AffordableVehiclePriceInput): number {
    if (
        !Number.isFinite(maximumMonthlyPayment) ||
        maximumMonthlyPayment <= 0 ||
        !Number.isFinite(downPayment) ||
        downPayment < 0 ||
        !Number.isFinite(termMonths) ||
        termMonths <= 0 ||
        !Number.isFinite(aprPercent) ||
        aprPercent < 0
    ) {
        return 0;
    }

    const monthlyRate = aprPercent / 100 / 12;
    const financedAmount =
        monthlyRate === 0
            ? maximumMonthlyPayment * termMonths
            : maximumMonthlyPayment * ((1 - (1 + monthlyRate) ** -termMonths) / monthlyRate);

    return Math.max(0, financedAmount + downPayment);
}
